import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { InquiryErrors, InquiryField, InquiryTopic } from '@/lib/inquiry'

import { useId, useState } from 'react'
import { CircleCheck, LoaderCircle, Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { INQUIRY_LIMITS, INQUIRY_TOPICS, validateInquiry } from '@/lib/inquiry'
import { contactInfo } from '@/constants/site'

interface InquiryFormProps {
    product?: { id: string, name: string } | null
    options?: string[]
    defaultTopic?: InquiryTopic
    className?: string
}

interface FormValues {
    name: string
    phone: string
    email: string
    city: string
    topic: string
    option: string
    message: string
    company: string
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error'

interface InquiryResponse {
    ok: boolean
    message?: string
    errors?: InquiryErrors
}

const FIELD_ORDER: InquiryField[] = ['name', 'phone', 'email', 'city', 'topic', 'message']

const selectClassName = 'h-12 w-full rounded-xl border border-input bg-card px-4 text-base text-foreground outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20'

export const InquiryForm: FC<InquiryFormProps> = ({
    product = null,
    options = [],
    defaultTopic,
    className
}): ReactNode => {
    const formId = useId()
    const initialTopic = defaultTopic ?? (product ? 'سفارش محصول' : 'مشاوره خرید')

    const createInitialValues = (): FormValues => ({
        name: '',
        phone: '',
        email: '',
        city: '',
        topic: initialTopic,
        option: '',
        message: '',
        company: ''
    })

    const [values, setValues] = useState<FormValues>(createInitialValues)
    const [errors, setErrors] = useState<InquiryErrors>({})
    const [status, setStatus] = useState<SubmitStatus>('idle')
    const [serverMessage, setServerMessage] = useState('')

    const fieldId = (field: string) => `${formId}-${field}`

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = event.target

        setValues((current) => ({ ...current, [name]: value }))

        if (name in errors) {
            setErrors((current) => {
                const next = { ...current }
                delete next[name as InquiryField]
                return next
            })
        }
    }

    const focusFirstError = (fieldErrors: InquiryErrors) => {
        const firstField = FIELD_ORDER.find((field) => fieldErrors[field])
        if (firstField) document.getElementById(fieldId(firstField))?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const fieldErrors = validateInquiry(values)

        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors)
            focusFirstError(fieldErrors)
            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/inquiries', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, productId: product?.id ?? '' })
            })

            const data = await response.json().catch(() => null) as InquiryResponse | null

            if (response.ok && data?.ok) {
                setStatus('success')
                setValues(createInitialValues())
                return
            }

            if (data?.errors && Object.keys(data.errors).length > 0) {
                setErrors(data.errors)
                focusFirstError(data.errors)
            }

            setServerMessage(data?.message ?? 'ثبت درخواست انجام نشد. لطفاً دوباره تلاش کنید.')
            setStatus('error')
        }
        catch {
            setServerMessage('ارتباط برقرار نشد. اتصال اینترنت خود را بررسی کنید و دوباره تلاش کنید.')
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <div
                role='status'
                className={cn('flex flex-col items-center rounded-3xl border border-border bg-card px-6 py-14 text-center animate-in fade-in zoom-in-95 duration-300', className)}
            >
                <span className='flex size-14 items-center justify-center rounded-full bg-accent text-brand'>
                    <CircleCheck className='size-7' aria-hidden='true' />
                </span>

                <h3 className='mt-6 font-heading text-3xl font-semibold'>
                    درخواست شما ثبت شد
                </h3>

                <p className='mt-3 max-w-md text-sm leading-7 text-muted-foreground'>
                    همکاران ما به‌زودی با شماره‌ای که وارد کردید تماس می‌گیرند. اگر عجله دارید، همین حالا با شوروم تماس بگیرید.
                </p>

                <div className='mt-7 flex flex-wrap justify-center gap-3'>
                    <a href={contactInfo.phoneHref} className={buttonVariants()}>
                        تماس با شوروم
                    </a>

                    <Button variant='outline' onClick={() => setStatus('idle')}>
                        ثبت درخواست دیگر
                    </Button>
                </div>
            </div>
        )
    }

    const isSubmitting = status === 'submitting'

    const renderError = (field: InquiryField) => errors[field] && (
        <p id={fieldId(`${field}-error`)} className='mt-2 text-sm text-destructive'>
            {errors[field]}
        </p>
    )

    const describedBy = (field: InquiryField, hint?: string) => {
        const ids = [errors[field] ? fieldId(`${field}-error`) : '', hint ?? ''].filter(Boolean)
        return ids.length > 0 ? ids.join(' ') : undefined
    }

    return (
        <form noValidate onSubmit={handleSubmit} className={cn('relative grid gap-5 sm:grid-cols-2', className)}>
            <div>
                <Label htmlFor={fieldId('name')}>
                    نام و نام خانوادگی
                    <span className='text-brand' aria-hidden='true'>*</span>
                </Label>

                <Input
                    id={fieldId('name')}
                    name='name'
                    autoComplete='name'
                    required
                    maxLength={INQUIRY_LIMITS.name}
                    value={values.name}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={describedBy('name')}
                    placeholder='مثلاً سارا محمدی'
                    className='mt-2.5'
                />

                {renderError('name')}
            </div>

            <div>
                <Label htmlFor={fieldId('phone')}>
                    شماره تماس
                    <span className='text-brand' aria-hidden='true'>*</span>
                </Label>

                <Input
                    id={fieldId('phone')}
                    name='phone'
                    type='tel'
                    inputMode='tel'
                    autoComplete='tel'
                    dir='ltr'
                    required
                    maxLength={20}
                    value={values.phone}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={describedBy('phone')}
                    placeholder='۰۹۱۲ ۳۴۵ ۶۷۸۹'
                    className='mt-2.5 text-right placeholder:text-right'
                />

                {renderError('phone')}
            </div>

            <div>
                <Label htmlFor={fieldId('email')}>
                    ایمیل
                    <span className='text-xs font-normal text-muted-foreground'>(اختیاری)</span>
                </Label>

                <Input
                    id={fieldId('email')}
                    name='email'
                    type='email'
                    autoComplete='email'
                    dir='ltr'
                    maxLength={INQUIRY_LIMITS.email}
                    value={values.email}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={describedBy('email')}
                    placeholder='name@example.com'
                    className='mt-2.5 text-right placeholder:text-right'
                />

                {renderError('email')}
            </div>

            <div>
                <Label htmlFor={fieldId('city')}>
                    شهر
                    <span className='text-xs font-normal text-muted-foreground'>(اختیاری)</span>
                </Label>

                <Input
                    id={fieldId('city')}
                    name='city'
                    autoComplete='address-level2'
                    maxLength={INQUIRY_LIMITS.city}
                    value={values.city}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.city)}
                    aria-describedby={describedBy('city')}
                    placeholder='مثلاً تهران'
                    className='mt-2.5'
                />

                {renderError('city')}
            </div>

            <div className={cn(options.length === 0 && 'sm:col-span-2')}>
                <Label htmlFor={fieldId('topic')}>
                    موضوع درخواست
                </Label>

                <select
                    id={fieldId('topic')}
                    name='topic'
                    value={values.topic}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.topic)}
                    aria-describedby={describedBy('topic')}
                    className={cn(selectClassName, 'mt-2.5')}
                >
                    {INQUIRY_TOPICS.map((topic) => (
                        <option key={topic} value={topic}>
                            {topic}
                        </option>
                    ))}
                </select>

                {renderError('topic')}
            </div>

            {options.length > 0 && (
                <div>
                    <Label htmlFor={fieldId('option')}>
                        رنگ یا روکش موردنظر
                    </Label>

                    <select
                        id={fieldId('option')}
                        name='option'
                        value={values.option}
                        onChange={handleChange}
                        className={cn(selectClassName, 'mt-2.5')}
                    >
                        <option value=''>
                            هنوز انتخاب نکرده‌ام
                        </option>

                        {options.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className='sm:col-span-2'>
                <Label htmlFor={fieldId('message')}>
                    توضیحات
                    <span className='text-xs font-normal text-muted-foreground'>(اختیاری)</span>
                </Label>

                <Textarea
                    id={fieldId('message')}
                    name='message'
                    maxLength={INQUIRY_LIMITS.message}
                    value={values.message}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={describedBy('message', fieldId('message-hint'))}
                    placeholder={product ? 'ابعاد فضا، تعداد، زمان تحویل موردنظر یا هر سؤالی دربارهٔ این محصول' : 'فضای موردنظر، سبک دلخواه، بودجه یا هر سؤالی که دارید'}
                    className='mt-2.5'
                />

                <p id={fieldId('message-hint')} className='mt-2 text-xs text-muted-foreground'>
                    هرچه جزئیات بیشتری بنویسید، مشاوره دقیق‌تری می‌گیرید.
                </p>

                {renderError('message')}
            </div>

            <div aria-hidden='true' className='pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0'>
                <label htmlFor={fieldId('company')}>
                    شرکت
                </label>

                <input
                    id={fieldId('company')}
                    name='company'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.company}
                    onChange={handleChange}
                />
            </div>

            <div className='flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between'>
                <p className='text-xs leading-6 text-muted-foreground'>
                    اطلاعات شما فقط برای پیگیری همین درخواست استفاده می‌شود.
                </p>

                <Button type='submit' size='lg' disabled={isSubmitting} className='w-full sm:w-auto'>
                    {isSubmitting ? (
                        <>
                            <LoaderCircle className='size-5 animate-spin' aria-hidden='true' />
                            در حال ارسال
                        </>
                    ) : (
                        <>
                            ثبت درخواست
                            <Send className='size-4 -scale-x-100' aria-hidden='true' />
                        </>
                    )}
                </Button>
            </div>

            {status === 'error' && serverMessage && (
                <p role='alert' className='rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm leading-7 text-destructive sm:col-span-2'>
                    {serverMessage}
                    {' '}
                    همچنین می‌توانید با شمارهٔ
                    {' '}
                    <a href={contactInfo.phoneHref} className='font-medium underline underline-offset-4'>
                        <span dir='ltr'>
                            {contactInfo.phone}
                        </span>
                    </a>
                    {' '}
                    تماس بگیرید.
                </p>
            )}
        </form>
    )
}
